/**
 * Glossed-term mark (brief-as-publication P2).
 *
 * Tags a house or statistical term where it appears in prose, carrying its definition, so a reader
 * meets the explanation at the point of confusion rather than hunting a glossary at the bottom.
 *
 * The definition travels ON the mark rather than being looked up at render time, deliberately: a
 * brief is an audit record, and what `verdictV5` meant on 2026-08-03 must stay legible even after
 * the glossary is revised. A term whose meaning changes silently under a published document is the
 * same class of defect as an editable run record.
 *
 * INTERNAL PM file; never re-exported from `index.ts`.
 */

import type { MarkSpec } from 'prosemirror-model';

export const TERM_MARK = 'term';

export const termMark: MarkSpec = {
  attrs: {
    /** Canonical key, e.g. "verdictV5" — what the glossary file is keyed by. */
    term: { default: '' },
    /** The definition AS OF publication. Frozen with the document, not resolved at read time. */
    definition: { default: '' },
  },
  // Non-inclusive: typing after a glossed term must not silently extend the gloss over new text.
  inclusive: false,
  parseDOM: [
    {
      tag: 'span[data-term]',
      getAttrs: (dom) => {
        const el = dom as HTMLElement;
        return {
          term: el.getAttribute('data-term') ?? '',
          definition: el.getAttribute('data-definition') ?? '',
        };
      },
    },
  ],
  toDOM: (mark) => [
    'span',
    {
      'data-term': mark.attrs.term,
      'data-definition': mark.attrs.definition,
      // `title` gives every host — including one with no custom renderer — a working tooltip.
      title: mark.attrs.definition,
      class: 'docit-term',
    },
    0,
  ],
};
